import { useEffect, useMemo, useState, useCallback } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  fetchAccountGroupOptions,
  fetchAccountOptions,
  fetchAccountStatement
} from '../../../services/reportAccountStatement';
import './AccountStatementPage.css';

const UI_TEXT = {
  'tr-TR': {
    title: 'Hesap Ekstresi',
    description: 'Yalnizca muhasebe fisi yuklenen donemlerde, accounting_voucher_lines tablosundan hesap hareketlerini raporlar.',
    list: 'Listele',
    listing: 'Listeleniyor...',
    filterType: 'Filtre Turu',
    filterAccount: 'Hesap',
    filterGroup: 'Hesap Grubu',
    account: 'Hesap',
    accountPlaceholder: 'Hesap seciniz',
    group: 'Hesap Grubu',
    groupPlaceholder: 'Hesap grubu seciniz',
    startDate: 'Baslangic Tarihi',
    endDate: 'Bitis Tarihi',
    pageSize: 'Sayfa Boyutu',
    totalDebit: 'Toplam Borc',
    totalCredit: 'Toplam Alacak',
    finalBalance: 'Son Bakiye',
    movementCount: 'Hareket Sayisi',
    colDate: 'Tarih',
    colVoucherNo: 'Fis No',
    colAccount: 'Hesap',
    colDescription: 'Aciklama',
    colDebit: 'Borc',
    colCredit: 'Alacak',
    colBalance: 'Bakiye',
    loadingData: 'Veriler yukleniyor...',
    noRecords: 'Gosterilecek hesap ekstresi kaydi yok.',
    prev: 'Onceki',
    next: 'Sonraki',
    pageInfo: 'Sayfa {{page}} / {{totalPages}}',
    optionsLoadError: 'Filtre listeleri yuklenemedi.',
    selectCompanyPeriod: 'Lutfen sirket ve donem secin.',
    selectFilter: 'Lutfen hesap veya hesap grubu secin.',
    fetchError: 'Hesap ekstresi verisi alinamadi.'
  },
  'en-US': {
    title: 'Account Statement',
    description: 'Reports account movements from accounting_voucher_lines table only for periods where accounting vouchers are uploaded.',
    list: 'List',
    listing: 'Listing...',
    filterType: 'Filter Type',
    filterAccount: 'Account',
    filterGroup: 'Account Group',
    account: 'Account',
    accountPlaceholder: 'Select account',
    group: 'Account Group',
    groupPlaceholder: 'Select account group',
    startDate: 'Start Date',
    endDate: 'End Date',
    pageSize: 'Page Size',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    finalBalance: 'Final Balance',
    movementCount: 'Movement Count',
    colDate: 'Date',
    colVoucherNo: 'Voucher No',
    colAccount: 'Account',
    colDescription: 'Description',
    colDebit: 'Debit',
    colCredit: 'Credit',
    colBalance: 'Balance',
    loadingData: 'Loading data...',
    noRecords: 'No account statement records to display.',
    prev: 'Previous',
    next: 'Next',
    pageInfo: 'Page {{page}} / {{totalPages}}',
    optionsLoadError: 'Filter lists could not be loaded.',
    selectCompanyPeriod: 'Please select company and period.',
    selectFilter: 'Please select account or account group.',
    fetchError: 'Account statement data could not be fetched.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

function AccountStatementPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const formatMoney = useCallback((value) => {
    const numeric = Number(value || 0);
    return numeric.toLocaleString(localeKey, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [localeKey]);

  const formatDate = useCallback((value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString(localeKey);
  }, [localeKey]);

  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [filterType, setFilterType] = useState('account');
  const [accountCode, setAccountCode] = useState('');
  const [accountGroup, setAccountGroup] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [message, setMessage] = useState('');

  const hasSelection = Boolean(selection.companyId && selection.periodId);

  useEffect(() => {
    const loadOptions = async () => {
      if (!hasSelection) {
        setAccounts([]);
        setGroups([]);
        return;
      }
      setLoadingOptions(true);
      try {
        const [accountRows, groupRows] = await Promise.all([
          fetchAccountOptions(selection.companyId, selection.periodId),
          fetchAccountGroupOptions(selection.companyId, selection.periodId)
        ]);
        setAccounts(accountRows || []);
        setGroups(groupRows || []);
      } catch (error) {
        setAccounts([]);
        setGroups([]);
        setMessage(error instanceof Error ? error.message : t('optionsLoadError'));
      } finally {
        setLoadingOptions(false);
      }
    };

    setAccountCode('');
    setAccountGroup('');
    setStatement(null);
    setPage(1);
    setMessage('');
    loadOptions();
  }, [hasSelection, selection.companyId, selection.periodId, t]);

  const hasAccountFilter = filterType === 'account' ? Boolean(accountCode) : Boolean(accountGroup);

  const handleFetch = async (nextPage = page) => {
    if (!hasSelection) {
      setMessage(t('selectCompanyPeriod'));
      return;
    }
    if (!hasAccountFilter) {
      setMessage(t('selectFilter'));
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const response = await fetchAccountStatement({
        companyId: selection.companyId,
        periodId: selection.periodId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        accountCode: filterType === 'account' ? accountCode : undefined,
        accountGroup: filterType === 'group' ? accountGroup : undefined,
        page: nextPage,
        size
      });
      setStatement(response);
      setPage(nextPage);
    } catch (error) {
      setStatement(null);
      setMessage(error instanceof Error ? error.message : t('fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    if (!statement?.totals) return { debit: 0, credit: 0, balance: 0, total: 0 };
    return {
      debit: statement.totals.totalDebit || 0,
      credit: statement.totals.totalCredit || 0,
      balance: statement.totals.finalBalance || 0,
      total: statement.pagination?.total || 0
    };
  }, [statement]);

  const totalPages = statement?.pagination?.totalPages || 0;

  return (
    <div className="card account-statement-card">
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <button type="button" className="btn-primary" onClick={() => handleFetch(1)} disabled={!hasSelection || loading || loadingOptions}>
            {loading ? t('listing') : t('list')}
          </button>
        }
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="account-statement-filters">
        <label>
          {t('filterType')}
          <select
            value={filterType}
            onChange={(event) => {
              const nextType = event.target.value;
              setFilterType(nextType);
              setAccountCode('');
              setAccountGroup('');
              setStatement(null);
            }}
          >
            <option value="account">{t('filterAccount')}</option>
            <option value="group">{t('filterGroup')}</option>
          </select>
        </label>

        {filterType === 'account' ? (
          <label>
            {t('account')}
            <select value={accountCode} onChange={(event) => setAccountCode(event.target.value)} disabled={loadingOptions}>
              <option value="">{t('accountPlaceholder')}</option>
              {accounts.map((item) => (
                <option key={item.code} value={item.code}>{item.code} - {item.name}</option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            {t('group')}
            <select value={accountGroup} onChange={(event) => setAccountGroup(event.target.value)} disabled={loadingOptions}>
              <option value="">{t('groupPlaceholder')}</option>
              {groups.map((item) => (
                <option key={item.code} value={item.code}>{item.code} - {item.name}</option>
              ))}
            </select>
          </label>
        )}

        <label>
          {t('startDate')}
          <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </label>

        <label>
          {t('endDate')}
          <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </label>

        <label>
          {t('pageSize')}
          <select value={size} onChange={(event) => {
            const next = Number(event.target.value) || 100;
            setSize(next);
            setPage(1);
          }}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </label>
      </section>

      {message && <p className="account-statement-message">{message}</p>}

      <section className="account-statement-summary">
        <article><strong>{formatMoney(summary.debit)}</strong><span>{t('totalDebit')}</span></article>
        <article><strong>{formatMoney(summary.credit)}</strong><span>{t('totalCredit')}</span></article>
        <article><strong>{formatMoney(summary.balance)}</strong><span>{t('finalBalance')}</span></article>
        <article><strong>{summary.total}</strong><span>{t('movementCount')}</span></article>
      </section>

      <div className="account-statement-table-wrapper">
        <table className="account-statement-table">
          <thead>
            <tr>
              <th>{t('colDate')}</th>
              <th>{t('colVoucherNo')}</th>
              <th>{t('colAccount')}</th>
              <th>{t('colDescription')}</th>
              <th>{t('colDebit')}</th>
              <th>{t('colCredit')}</th>
              <th>{t('colBalance')}</th>
            </tr>
          </thead>
          <tbody>
            {!statement?.transactions?.length ? (
              <tr>
                <td colSpan={7} className="account-statement-empty">{loading ? t('loadingData') : t('noRecords')}</td>
              </tr>
            ) : (
              statement.transactions.map((row, index) => (
                <tr key={`${row.voucherNo}-${row.accountCode}-${index}`}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.voucherNo || '-'}</td>
                  <td>
                    <strong>{row.accountCode}</strong>
                    <div className="account-statement-account-name">{row.accountName || '-'}</div>
                  </td>
                  <td>{row.lineDescription || row.voucherDescription || '-'}</td>
                  <td className="account-statement-amount">{formatMoney(row.debit)}</td>
                  <td className="account-statement-amount">{formatMoney(row.credit)}</td>
                  <td className="account-statement-amount">{formatMoney(row.balance)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="account-statement-pagination">
        <button type="button" className="btn-secondary" disabled={loading || page <= 1 || totalPages === 0} onClick={() => handleFetch(page - 1)}>{t('prev')}</button>
        <span>{t('pageInfo').replace('{{page}}', String(totalPages === 0 ? 0 : page)).replace('{{totalPages}}', String(totalPages))}</span>
        <button type="button" className="btn-secondary" disabled={loading || totalPages === 0 || page >= totalPages} onClick={() => handleFetch(page + 1)}>{t('next')}</button>
      </section>
    </div>
  );
}

export default AccountStatementPage;
